import { useEffect, useMemo, useRef, useState } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { ChevronUpIcon, ExclamationCircleIcon } from "@heroicons/react/solid";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import { IMedia } from "../../../../model/unityObject";
import ImageCard from "../../../gallery/components/ImageCard";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-random-quizz stores a POOL of independent mini-quizzes, sampled at
// runtime. The single field `questions` is shaped:
//   questions: [{
//     text, answers: [{ text, imageId, is_right }],
//     winText, loseText, imageAnswers, allowMultiple
//   }]
//
// Round-trip (DIFFERS from the other three quizz editors — see
// docs/plans/partial/node-form-quizz-json-editors.md §3.4):
//   • base content object → questions: [ ...full objects... ] (structural
//     source of truth; imageId/is_right/flags shared across languages, the
//     various .text fields hold source-language text)
//   • per-language PLD     → list_question_random_quizz_data:
//     [{ question: {...full object...} }] — the WHOLE element wrapped under
//     `question` (NOT a text-only projection, unlike sort/image-multiple).
// NodeForm seeds RHF from the content structure overlaid with the active
// language's PLD text (by position), and buildUpdatedProjectLanguageData wraps
// each element back under `question` on the PLD write. The full object array
// round-trips on the content PUT (source language only). NOTE: this type is
// EXCLUDED from the backend afterUpdate PLD sync (src/index.js), so the
// frontend's wrapped PLD PUT is the SOLE PLD writer.

export type RandomAnswer = {
  text: string;
  imageId: number | null;
  is_right: boolean;
};

export type RandomQuestion = {
  text: string;
  answers: RandomAnswer[];
  winText: string;
  loseText: string;
  imageAnswers: boolean;
  allowMultiple: boolean;
};

// minItems from the slopify jsonSchema (questions.minItems / answers.minItems);
// soft max from the documented generation ranges ("3-5 questions", "3-4
// answers"). Existing data beyond the cap is preserved — only Add is capped.
const MIN_QUESTIONS = 3;
const MAX_QUESTIONS = 5;
const MIN_ANSWERS = 3;
const MAX_ANSWERS = 4;

const NOMEDIA_FALLBACK: IMedia = {
  createdAt: "",
  id: -1,
  name: "",
  label: "",
  organization: 3,
  s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
  type: null,
  updatedAt: "",
  filename: "NOMEDIA.png",
  tags: [],
  upload_status: "completed",
} as unknown as IMedia;

// Coerce a stored question into a fully-defaulted, editor-safe shape. Older /
// generated rows may store imageId as a string ("176") or omit flags.
const normalizeQuestion = (q: any): RandomQuestion => ({
  text: typeof q?.text === "string" ? q.text : "",
  winText: typeof q?.winText === "string" ? q.winText : "",
  loseText: typeof q?.loseText === "string" ? q.loseText : "",
  imageAnswers: !!q?.imageAnswers,
  allowMultiple: !!q?.allowMultiple,
  answers: Array.isArray(q?.answers)
    ? q.answers.map((a: any) => ({
        text: typeof a?.text === "string" ? a.text : "",
        imageId:
          a?.imageId === null || a?.imageId === undefined ? null : Number(a.imageId),
        is_right: !!a?.is_right,
      }))
    : [],
});

const emptyAnswer = (): RandomAnswer => ({ text: "", imageId: null, is_right: false });

const emptyQuestion = (): RandomQuestion => ({
  text: "",
  // Seed at the minimum answer count so a fresh question is structurally valid;
  // the author fills the text (empty text is a save-block).
  answers: Array.from({ length: MIN_ANSWERS }, emptyAnswer),
  winText: "",
  loseText: "",
  imageAnswers: false,
  allowMultiple: false,
});

type QuestionErrors = {
  // language-agnostic
  emptyText: boolean;
  // source-language only (structural)
  noCorrect: boolean;
  missingImage: boolean;
};

interface QuizRandomQuizzFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** Full media library, to resolve each answer's imageId to a thumbnail. */
  mediasList: IMedia[];
  /** Opens the shared gallery modal for answer (qIndex, aIndex). */
  onPickImage: (qIndex: number, aIndex: number) => void;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove/correctness/flags/image) are source-only. */
  isSourceLanguage: boolean;
  /** Optional per-answer character cap (none in the current schema). */
  maxLength?: number;
}

const QuizRandomQuizzField = ({
  control,
  setValue,
  label,
  mediasList,
  onPickImage,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: QuizRandomQuizzFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "questions" });
  const questions: RandomQuestion[] = useMemo(
    () => (Array.isArray(watched) ? watched.map(normalizeQuestion) : []),
    [watched],
  );

  // Text is editable in any language; structure (counts, correctness, flags,
  // images) is locked outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  // ── Accordion open-state ────────────────────────────────────────────────
  // Set of open question indices. In the SOURCE language the add/remove
  // handlers own this set entirely (no effect resets it, so they never fight).
  // Default: first card open for quick orientation.
  const [openIdx, setOpenIdx] = useState<Set<number>>(() => new Set([0]));
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [scrollTo, setScrollTo] = useState<number | null>(null);

  // Translation (target language): expand every card so the translator sees
  // all text fields at once without drilling in. Runs only when the language
  // flips or the seeded count changes.
  useEffect(() => {
    if (!isSourceLanguage) {
      setOpenIdx(new Set(questions.map((_, i) => i)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceLanguage, questions.length]);

  // Scroll a freshly-opened card into view (and focus its first field) once
  // its body has rendered.
  useEffect(() => {
    if (scrollTo == null) return;
    const el = cardRefs.current[scrollTo];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      const focusable = el.querySelector<HTMLElement>("textarea, input");
      focusable?.focus();
    }
    setScrollTo(null);
  }, [scrollTo, openIdx]);

  const isOpen = (i: number) => openIdx.has(i);
  const toggleOpen = (i: number) =>
    setOpenIdx((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const expandAll = () => setOpenIdx(new Set(questions.map((_, i) => i)));
  const collapseAll = () => setOpenIdx(new Set());
  const openAndScroll = (i: number) => {
    setOpenIdx((prev) => new Set(prev).add(i));
    setScrollTo(i);
  };

  // ── Writes (RHF is the single source of truth) ──────────────────────────
  const write = (next: RandomQuestion[]) =>
    setValue("questions", next, { shouldDirty: true });

  const updateQuestion = (qIndex: number, patch: Partial<RandomQuestion>) =>
    write(questions.map((q, i) => (i === qIndex ? { ...q, ...patch } : q)));

  const updateAnswers = (
    qIndex: number,
    mutate: (answers: RandomAnswer[]) => RandomAnswer[],
  ) =>
    write(
      questions.map((q, i) =>
        i === qIndex ? { ...q, answers: mutate(q.answers) } : q,
      ),
    );

  const handleQuestionText = (qIndex: number, value: string) =>
    updateQuestion(qIndex, { text: value });

  const handleWinText = (qIndex: number, value: string) =>
    updateQuestion(qIndex, { winText: value });

  const handleLoseText = (qIndex: number, value: string) =>
    updateQuestion(qIndex, { loseText: value });

  const handleAnswerText = (qIndex: number, aIndex: number, value: string) =>
    updateAnswers(qIndex, (answers) =>
      answers.map((a, i) => (i === aIndex ? { ...a, text: value } : a)),
    );

  const handleToggleAllowMultiple = (qIndex: number) => {
    const q = questions[qIndex];
    const nextAllow = !q.allowMultiple;
    let answers = q.answers;
    // Turning OFF with several correct: coerce to single by keeping the FIRST
    // currently-correct answer (none correct ⇒ leave all false), mirroring
    // content-quizz-3's has_multiple_answers behaviour.
    if (!nextAllow) {
      const firstCorrect = answers.findIndex((a) => a.is_right);
      answers = answers.map((a, i) => ({ ...a, is_right: i === firstCorrect }));
    }
    updateQuestion(qIndex, { allowMultiple: nextAllow, answers });
  };

  const handleToggleImageAnswers = (qIndex: number) =>
    // Non-destructive: only flips the flag. Existing imageIds are preserved so
    // toggling back restores the images without re-picking.
    updateQuestion(qIndex, { imageAnswers: !questions[qIndex].imageAnswers });

  const handleToggleCorrect = (qIndex: number, aIndex: number) => {
    const q = questions[qIndex];
    if (q.allowMultiple) {
      // Many-correct: flip just this answer.
      updateAnswers(qIndex, (answers) =>
        answers.map((a, i) => (i === aIndex ? { ...a, is_right: !a.is_right } : a)),
      );
    } else {
      // Single-correct: this one true, the rest false.
      updateAnswers(qIndex, (answers) =>
        answers.map((a, i) => ({ ...a, is_right: i === aIndex })),
      );
    }
  };

  const handleAddAnswer = (qIndex: number) =>
    updateAnswers(qIndex, (answers) =>
      answers.length < MAX_ANSWERS ? [...answers, emptyAnswer()] : answers,
    );

  const handleRemoveAnswer = (qIndex: number, aIndex: number) =>
    updateAnswers(qIndex, (answers) =>
      answers.length > MIN_ANSWERS ? answers.filter((_, i) => i !== aIndex) : answers,
    );

  const handleAddQuestion = () => {
    if (questions.length >= MAX_QUESTIONS) return;
    const newIndex = questions.length;
    write([...questions, emptyQuestion()]);
    openAndScroll(newIndex);
  };

  const handleRemoveQuestion = (qIndex: number) => {
    if (questions.length <= MIN_QUESTIONS) return;
    write(questions.filter((_, i) => i !== qIndex));
    // Re-map open indices: drop the removed one, shift everything after it down.
    setOpenIdx((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < qIndex) next.add(i);
        else if (i > qIndex) next.add(i - 1);
      });
      return next;
    });
  };

  // ── Validation (display + summary) ──────────────────────────────────────
  // Mirrors the save-block flags computed in NodeForm: empty-text is
  // language-agnostic; no-correct and missing-image are structural → only
  // surfaced (and blocking) in the source language.
  const errorsFor = (q: RandomQuestion): QuestionErrors => ({
    emptyText:
      !disabled &&
      (!(q.text ?? "").trim() || q.answers.some((a) => !(a.text ?? "").trim())),
    noCorrect: !disabled && isSourceLanguage && !q.answers.some((a) => a.is_right),
    missingImage:
      !disabled &&
      isSourceLanguage &&
      q.imageAnswers &&
      q.answers.some((a) => !(Number(a.imageId) > 0)),
  });

  const hasAnyError = (e: QuestionErrors) =>
    e.emptyText || e.noCorrect || e.missingImage;

  const errorMessages = (e: QuestionErrors): string[] => {
    const msgs: string[] = [];
    if (e.noCorrect) msgs.push(t("pages.project.editNode.quizzNeedsCorrectAnswer"));
    if (e.missingImage) msgs.push(t("pages.project.editNode.quizzImageNeedsImage"));
    if (e.emptyText) msgs.push(t("pages.project.editNode.quizzRandomNeedsText"));
    return msgs;
  };

  const questionErrors = questions.map(errorsFor);
  const problemQuestions = questionErrors
    .map((e, i) => ({ i, e }))
    .filter(({ e }) => hasAnyError(e));

  const canAddQuestion = !structureLocked && questions.length < MAX_QUESTIONS;
  const canRemoveQuestion = !structureLocked && questions.length > MIN_QUESTIONS;

  const minQuestionsHint = t("pages.project.editNode.quizzRandomMinQuestionsHint") as string;
  const maxQuestionsHint = t("pages.project.editNode.quizzRandomMaxQuestionsHint") as string;
  const minAnswersHint = t("pages.project.editNode.quizzRandomMinAnswersHint") as string;
  const maxAnswersHint = t("pages.project.editNode.quizzRandomMaxAnswersHint") as string;

  return (
    <div className="w-full mt-4">
      {/* Header: label + Expand/Collapse all + Add question */}
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center gap-2">
          {questions.length > 1 && (
            <div className="flex items-center gap-1 text-xs">
              <button
                type="button"
                onClick={expandAll}
                className="text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
              >
                {t("pages.project.editNode.quizzRandomExpandAll")}
              </button>
              <span className="text-gray-300">·</span>
              <button
                type="button"
                onClick={collapseAll}
                className="text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
              >
                {t("pages.project.editNode.quizzRandomCollapseAll")}
              </button>
            </div>
          )}
          <Tooltip
            message={!canAddQuestion && !structureLocked ? maxQuestionsHint : ""}
            className="shrink-0"
            classNameChildren="right-0"
          >
            <button
              type="button"
              onClick={handleAddQuestion}
              disabled={!canAddQuestion}
              className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <span>{t("pages.project.editNode.quizzRandomAddQuestion")}</span>
            </button>
          </Tooltip>
        </div>
      </div>

      {!isSourceLanguage && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {/* Error summary (reserved area). Lists every question with a blocking
          problem as a click-to-expand chip, so a save-block is never hidden
          inside a collapsed card. */}
      {problemQuestions.length > 0 && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 text-xs font-semibold text-red-700 mb-1.5">
            <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
            <span>{t("pages.project.editNode.quizzRandomFixBeforeSaving")}</span>
          </div>
          <ul className="flex flex-col gap-1">
            {problemQuestions.map(({ i, e }) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => openAndScroll(i)}
                  className="text-xs text-red-700 hover:underline text-left"
                >
                  {t("pages.project.editNode.quizzRandomQuestionLabel", { n: i + 1 })}
                  {" — "}
                  {errorMessages(e).join(" · ")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        {questions.map((q, qIndex) => {
          const open = isOpen(qIndex);
          const errs = questionErrors[qIndex];
          const errMsgs = errorMessages(errs);
          const canAddAnswer = !structureLocked && q.answers.length < MAX_ANSWERS;
          const canRemoveAnswer = !structureLocked && q.answers.length > MIN_ANSWERS;
          const preview = (q.text ?? "").trim();
          return (
            <div
              key={qIndex}
              ref={(el) => (cardRefs.current[qIndex] = el)}
              className="border border-gray-200 rounded-lg bg-white"
            >
              {/* Accordion header. Corners are rounded here (not via
                  overflow-hidden on the card) so the inline Tooltips on the
                  badge / remove button aren't clipped. */}
              <div
                className={`flex items-center gap-2 px-3 py-2.5 bg-gray-50 ${
                  open ? "rounded-t-lg" : "rounded-lg"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleOpen(qIndex)}
                  aria-expanded={open}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                >
                  <ChevronUpIcon
                    className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                      open ? "" : "rotate-180"
                    }`}
                  />
                  <span className="text-sm font-medium text-gray-700 shrink-0">
                    {t("pages.project.editNode.quizzRandomQuestionLabel", { n: qIndex + 1 })}
                  </span>
                  {!open && preview && (
                    <span className="text-xs text-gray-400 truncate">{preview}</span>
                  )}
                </button>

                {/* Error indicator: only on a COLLAPSED card, so you know it
                    needs attention without opening it. When open, the inline
                    error below the answers carries the message — no double-up. */}
                {!open && errMsgs.length > 0 && (
                  <Tooltip message={errMsgs.join(" · ")} className="shrink-0" classNameChildren="right-0">
                    <span className="flex items-center gap-1 text-red-600">
                      <ExclamationCircleIcon className="h-4 w-4" />
                      <span className="text-xs font-semibold">{errMsgs.length}</span>
                    </span>
                  </Tooltip>
                )}

                <Tooltip
                  message={!canRemoveQuestion && !structureLocked ? minQuestionsHint : ""}
                  className="shrink-0"
                  classNameChildren="right-0"
                >
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(qIndex)}
                    disabled={!canRemoveQuestion}
                    className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={t("general.delete") as string}
                  >
                    <MdDeleteOutline size={18} />
                  </button>
                </Tooltip>
              </div>

              {/* Accordion body (only rendered when open → lighter DOM) */}
              {open && (
                <div className="p-3 flex flex-col gap-3">
                  {/* Question prompt. The card header already says "Question N",
                      so this label describes the field, not the number. */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t("pages.project.editNode.quizzRandomQuestionTextLabel")}
                    </label>
                    <textarea
                      value={q.text ?? ""}
                      onChange={(e) => handleQuestionText(qIndex, e.target.value)}
                      disabled={disabled}
                      rows={2}
                      className="dynamic-field appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>

                  {/* Per-question structural toggles (source-only) */}
                  <div className="flex flex-col gap-2">
                    <label
                      className="flex items-center gap-2"
                      title={t("pages.project.editNode.quizzRandomImageAnswersHint") as string}
                    >
                      <input
                        type="checkbox"
                        checked={q.imageAnswers}
                        onChange={() => handleToggleImageAnswers(qIndex)}
                        disabled={structureLocked}
                        className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                      />
                      <span className="text-xs text-gray-600">
                        {t("pages.project.editNode.quizzRandomImageAnswersLabel")}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={q.allowMultiple}
                        onChange={() => handleToggleAllowMultiple(qIndex)}
                        disabled={structureLocked}
                        className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                      />
                      <span className="text-xs text-gray-600">
                        {t("pages.project.editNode.quizzRandomAllowMultipleLabel")}
                      </span>
                    </label>
                  </div>

                  {/* Answers */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-gray-600">
                        {t("pages.project.editNode.quizzMarkCorrect")}
                      </span>
                      <Tooltip
                        message={!canAddAnswer && !structureLocked ? maxAnswersHint : ""}
                        className="shrink-0"
                        classNameChildren="right-0"
                      >
                        <button
                          type="button"
                          onClick={() => handleAddAnswer(qIndex)}
                          disabled={!canAddAnswer}
                          className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          <span>{t("pages.project.editNode.quizzAddAnswer")}</span>
                        </button>
                      </Tooltip>
                    </div>

                    {q.answers.map((answer, aIndex) => {
                      const media =
                        mediasList.find((m) => Number(m.id) === Number(answer.imageId)) ??
                        NOMEDIA_FALLBACK;
                      return (
                        <div
                          key={aIndex}
                          className="border border-gray-100 rounded-md p-2 flex items-start gap-3"
                        >
                          {q.imageAnswers && (
                            <div className="w-28 shrink-0">
                              <ImageCard
                                media={media}
                                currentMedia={
                                  Number(answer.imageId) > 0 ? Number(answer.imageId) : undefined
                                }
                                isMediaPickerMode={false}
                                showThreeDotsMenu={false}
                                disabled={structureLocked}
                                handleButtonClick={() => onPickImage(qIndex, aIndex)}
                                handleCardClick={() => {
                                  if (!structureLocked) onPickImage(qIndex, aIndex);
                                }}
                                className="w-28"
                              />
                            </div>
                          )}

                          <label
                            className="flex items-center pt-2 shrink-0"
                            title={t("pages.project.editNode.quizzMarkCorrect") as string}
                          >
                            <input
                              type={q.allowMultiple ? "checkbox" : "radio"}
                              name={`q-${qIndex}-correct`}
                              checked={!!answer.is_right}
                              onChange={() => handleToggleCorrect(qIndex, aIndex)}
                              disabled={structureLocked}
                              className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                            />
                          </label>

                          <div className="flex-1 min-w-0 pt-1">
                            <Input
                              type="text"
                              value={answer.text ?? ""}
                              onChange={(e) => handleAnswerText(qIndex, aIndex, e.target.value)}
                              disabled={disabled}
                              maxLength={maxLength}
                              placeholder={
                                t("pages.project.editNode.quizzAnswerPlaceholder") as string
                              }
                              className="dynamic-field"
                            />
                          </div>

                          <Tooltip
                            message={!canRemoveAnswer && !structureLocked ? minAnswersHint : ""}
                            className="shrink-0"
                            classNameChildren="right-0"
                          >
                            <button
                              type="button"
                              onClick={() => handleRemoveAnswer(qIndex, aIndex)}
                              disabled={!canRemoveAnswer}
                              className="text-red-500 p-1 pt-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label={t("general.delete") as string}
                            >
                              <MdDeleteOutline size={18} />
                            </button>
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>

                  {/* Inline error, next to the answers it usually concerns
                      (no correct answer / missing image / empty text). Shown
                      only while the card is open; the collapsed-card badge above
                      covers the closed state. */}
                  {errMsgs.length > 0 && (
                    <p className="text-xs font-medium text-red-600">{errMsgs.join(" · ")}</p>
                  )}

                  {/* Optional win / lose texts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t("pages.project.editNode.quizzRandomWinText")}
                      </label>
                      <textarea
                        value={q.winText ?? ""}
                        onChange={(e) => handleWinText(qIndex, e.target.value)}
                        disabled={disabled}
                        rows={2}
                        className="dynamic-field appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t("pages.project.editNode.quizzRandomLoseText")}
                      </label>
                      <textarea
                        value={q.loseText ?? ""}
                        onChange={(e) => handleLoseText(qIndex, e.target.value)}
                        disabled={disabled}
                        rows={2}
                        className="dynamic-field appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizRandomQuizzField;
